import { isThisYear } from 'date-fns';
import { momentFormat } from '#helper';
import { warehouseEventType } from '#type';

const removeFalsy = obj => {
  const newObj = {};
  Object.keys(obj).forEach(key => {
    if (obj[key]) { newObj[key] = obj[key]; }
  });
  return newObj;
};

/**
 * Returns object based on url search params.
 * @param {string} queryString - search query.
 * @returns {Object} Search query state object.
 */

const parseQueryState = queryString => {
  const params = new URLSearchParams(queryString);

  return Object.fromEntries(params.entries());
};

/**
 * Build search params
 * @param {string} params - current search params.
 * @param {Array|Object} newParams - new search params.
 * @returns {string} - merged search params
 */

export const getSearchParams = (params, newParams) => {
  let searchParams;

  if (!newParams) {
    return '';
  }

  if (Array.isArray(newParams)) {
    const parsedCurrUrl = parseQueryState(params);
    searchParams = new URLSearchParams({
      ...parsedCurrUrl,
      ...{ warehouse: '', appliance: '', device: '', user: '' }
    });
    const param = {
      warehouse: [],
      appliance: [],
      device: [],
      user: [],
      page: []
    };
    newParams.forEach(el => {
      const { type, value } = el;
      if (searchParams.has(type)) {
        param[type].push(value);
        searchParams.set(type, `${param[type].join(',')}`);
      } else {
        searchParams.set(type, value);
      }
    });
  } else {
    searchParams = new URLSearchParams(params);
    const { filterType, value, end, start } = newParams;

    if (filterType && value) {
      searchParams.set(filterType, value);
    }
    if (start && end) {
      searchParams.set('start', start);
      searchParams.set('end', end);
    }
  }

  const currentUrl = parseQueryState(params);
  const newUrl = parseQueryState(searchParams.toString());
  const mergedUrls = { ...currentUrl, ...newUrl };
  const url = new URLSearchParams(removeFalsy(mergedUrls));

  return `${url.toString()}`;
};

export const getEventDescription = (item, account, isCurrent) => {
  const {
    warehouse,
    appliance,
    applianceGroup,
    alert,
    actor,
    device,
    event: { id },
    data
  } = item;

  const {
    CREATE_WAREHOUSE,
    EDIT_WAREHOUSE,
    DELETE_WAREHOUSE,
    CREATE_APPLIANCE,
    EDIT_APPLIANCE,
    DELETE_APPLIANCE,
    ADD_APPLIANCE_GROUP,
    EDIT_APPLIANCE_GROUP,
    DELETE_APPLIANCE_GROUP,
    STOP_APPLIANCE,
    ADD_DEVICE,
    REMOVE_DEVICE,
    ADD_APPLIANCE_GROUP_ALERT,
    EDIT_APPLIANCE_GROUP_ALERT,
    REMOVE_APPLIANCE_GROUP_ALERT,
    TRIGGER_APPLIANCE_GROUP_ALERT,
    ADD_DEVICE_ALERT,
    EDIT_DEVICE_ALERT,
    REMOVE_DEVICE_ALERT,
    ADD_ALERT_CONFIRMATION,
    EDIT_ALERT_CONFIRMATION,
    REMOVE_ALERT_CONFIRMATION
  } = warehouseEventType;
  try {
    const isAdminAccess = account?.userType < 10;
    const firstAndLastName = (actor && isAdminAccess) ? ` by ${actor.firstName} ${actor.lastName}` : '';
    const applianceLabel = isCurrent ? '' : ` "${appliance?.label}"`;
    const applianceGroupLabel = applianceGroup?.label ? ` "${applianceGroup.label}" ` : '';
    const alertConfirmationLabel = data?.value;
    const alertLabel = data?.alert?.label;
    const alertTimestamp = data?.mqttMessageTimestamp ?
      momentFormat(data?.mqttMessageTimestamp, account, isThisYear(new Date(data?.mqttMessageTimestamp)) ? 'dateTimePartial' : 'dateTimeShort') : '';

    switch (id) {
      case CREATE_WAREHOUSE:
        return `Site "${warehouse.label}" was created${firstAndLastName}`;
      case EDIT_WAREHOUSE:
        return `Site "${warehouse.label}" was edited${firstAndLastName}`;
      case DELETE_WAREHOUSE:
        return `Site "${warehouse.label}" was removed${firstAndLastName}`;
      case CREATE_APPLIANCE:
        return `Application${applianceLabel} was added to site "${warehouse.label}"${firstAndLastName}`;
      case EDIT_APPLIANCE:
        return `Application${applianceLabel} was edited${firstAndLastName}`;
      case DELETE_APPLIANCE:
        return `Application${applianceLabel} was removed from site "${warehouse.label}"${firstAndLastName}`;
      case STOP_APPLIANCE:
        return `Application${applianceLabel} was stopped${firstAndLastName}`;
      case ADD_APPLIANCE_GROUP:
        return `Device group${applianceGroupLabel} was added to application${applianceLabel}${firstAndLastName}`;
      case EDIT_APPLIANCE_GROUP:
        return `Device group${applianceGroupLabel} was edited${firstAndLastName}`;
      case DELETE_APPLIANCE_GROUP:
        return `Device group${applianceGroupLabel} was removed from application${applianceLabel}${firstAndLastName}`;
      case ADD_DEVICE:
        return `Device ${device.title || device.uid} was added to application${applianceLabel}${firstAndLastName}`;
      case REMOVE_DEVICE:
        return `Device ${device.title || device.uid} was removed from application${applianceLabel}${firstAndLastName}`;
      case ADD_APPLIANCE_GROUP_ALERT:
        return `Alert "${alert.label}" was added to device group${applianceGroupLabel}${firstAndLastName}`;
      case EDIT_APPLIANCE_GROUP_ALERT:
        return `Alert "${alert.label}" was edited in device group${applianceGroupLabel}${firstAndLastName}`;
      case REMOVE_APPLIANCE_GROUP_ALERT:
        return `Alert "${alert.label}" was removed from device group${applianceGroupLabel}${firstAndLastName}`;
      case TRIGGER_APPLIANCE_GROUP_ALERT:
        return `Alert "${alert.label}" was triggered in application${applianceLabel} by device ${device.title || device.uid}`;
      case ADD_DEVICE_ALERT:
        return `Alert "${alert.label}" was added to device ${device.title || device.uid}${firstAndLastName}`;
      case EDIT_DEVICE_ALERT:
        return `Alert "${alert.label}" was edited in device ${device.title || device.uid}${firstAndLastName}`;
      case REMOVE_DEVICE_ALERT:
        return `Alert "${alert.label}" was removed from device ${device.title || device.uid}${firstAndLastName}`;
      case ADD_ALERT_CONFIRMATION:
        return `Verification "${alertConfirmationLabel}" was added for alert "${alertLabel}" triggered on ${alertTimestamp}`;
      case EDIT_ALERT_CONFIRMATION:
        return `Verification "${alertConfirmationLabel}" was edited for alert "${alertLabel}" triggered on ${alertTimestamp}`;
      case REMOVE_ALERT_CONFIRMATION:
        return `Verification was withdrawn from alert "${alertLabel}" triggered on ${alertTimestamp}`;
      default:
        return 'Not recognized event';
    }
  } catch (e) {
    return 'Not recognized event';
  }
};
